import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CalendarCheck, Check, X, RefreshCw, CalendarDays, List, Users } from 'lucide-react';
import { adminAPI } from '../api/client';
import { formatIstDate, formatIstMonthYear, istToday } from '../utils/formatIst';
import { useAuth } from '../context/AuthContext';
import { PRIVILEGES } from '../config/roles';
import { useGlobalSearch } from '../context/SearchContext';
import { matchesGlobalSearch } from '../utils/globalSearch';
import {
  defaultAttendanceMonth,
  daysInMonthYm,
  ymdForDay,
  monthBoundsYm,
} from '../utils/attendanceMonthly';

const STATUS_TABS = [
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
  { id: '', label: 'All' },
];

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
};

const DAY_CELL_STYLES = {
  approved: 'bg-emerald-500',
  pending: 'bg-amber-400 ring-1 ring-amber-600',
  rejected: 'bg-rose-300',
  none: 'bg-gray-100',
  future: 'bg-gray-50 border border-gray-100',
};

const VIEW_MODES = [
  { id: 'monthly', label: 'Monthly overview', icon: CalendarDays },
  { id: 'detail', label: 'Requests detail', icon: List },
];

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

function dayLeaveCellStatus(dayEntry, dayYmd, todayYmd) {
  if (!dayEntry) return dayYmd > todayYmd ? 'future' : 'none';
  return dayEntry.status || 'none';
}

function dayLeaveTooltip(dayEntry) {
  if (!dayEntry) return 'No leave';
  return `${cap(dayEntry.status)} • ${cap(dayEntry.leave_type)}\n${formatIstDate(dayEntry.from_date)} – ${formatIstDate(dayEntry.to_date)}`;
}

const LeaveManagement = () => {
  const { user, hasPrivilege } = useAuth();
  const canManageLeave = hasPrivilege(PRIVILEGES.MANAGE_LEAVE);
  const { query: globalSearch } = useGlobalSearch();

  const userRole = String(user?.role || '').toLowerCase();
  const isHierarchyViewer = userRole === 'supervisor' || userRole === 'manager';
  const repFilterLabel = isHierarchyViewer ? 'Reportee' : 'Representative';

  const [reps, setReps] = useState([]);
  const [items, setItems] = useState([]);
  const [monthSummary, setMonthSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('monthly');
  const [selectedMonth, setSelectedMonth] = useState(defaultAttendanceMonth());
  const [selectedRepId, setSelectedRepId] = useState('');
  const [status, setStatus] = useState('pending');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  const todayYmd = istToday();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const repsRes = await adminAPI.getAllReps();
      setReps(repsRes.data?.data || []);

      if (viewMode === 'monthly') {
        const sumRes = await adminAPI.getLeaveMonthSummary({
          month: selectedMonth,
          rep_id: selectedRepId || undefined,
          status: status || undefined,
        });
        setMonthSummary(sumRes.data?.data || null);
        setItems([]);
      } else {
        const bounds = !fromDate && !toDate ? monthBoundsYm(selectedMonth) : null;
        const res = await adminAPI.getLeaveRequests({
          status: status || undefined,
          rep_id: selectedRepId || undefined,
          from: fromDate || bounds?.start,
          to: toDate || bounds?.end,
        });
        setItems(res.data?.data || []);
        setMonthSummary(null);
      }
    } catch (e) {
      setError(e.message || 'Failed to load leave requests.');
      setItems([]);
      setMonthSummary(null);
    } finally {
      setLoading(false);
    }
  }, [viewMode, selectedMonth, selectedRepId, status, fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load]);

  const openRepDayDetail = (repId, dayYmd) => {
    setViewMode('detail');
    setSelectedRepId(String(repId));
    setFromDate(dayYmd);
    setToDate(dayYmd);
    setStatus('');
  };

  const openRepMonthDetail = (repId) => {
    const bounds = monthBoundsYm(selectedMonth);
    setViewMode('detail');
    setSelectedRepId(String(repId));
    setFromDate(bounds?.start || '');
    setToDate(bounds?.end || '');
    setStatus('');
  };

  const review = async (id, newStatus) => {
    let note = '';
    if (newStatus === 'rejected') {
      note = window.prompt('Reason for rejection (optional):') || '';
    }
    setBusyId(id);
    try {
      await adminAPI.reviewLeave(id, newStatus, note);
      await load();
    } catch (e) {
      alert(e.message || 'Could not update the leave request.');
    } finally {
      setBusyId(null);
    }
  };

  const filteredItems = useMemo(() => {
    return items.filter((r) =>
      matchesGlobalSearch(globalSearch, [
        r.rep_name,
        r.rep_email,
        r.leave_type,
        r.reason,
        r.status,
        r.from_date,
        r.to_date,
      ])
    );
  }, [items, globalSearch]);

  const monthlyRows = useMemo(() => {
    const rows = monthSummary?.reps || [];
    if (!globalSearch.trim()) return rows;
    return rows.filter((r) =>
      matchesGlobalSearch(globalSearch, [r.rep_name, r.rep_email])
    );
  }, [monthSummary, globalSearch]);

  const monthDayNums = useMemo(() => daysInMonthYm(selectedMonth), [selectedMonth]);

  const monthlyStats = useMemo(() => {
    const rows = monthSummary?.reps || [];
    const pending = rows.reduce(
      (s, r) => s + (r.requests || []).filter((req) => req.status === 'pending').length,
      0
    );
    const leaveDays = rows.reduce((s, r) => s + (r.leave_days || 0), 0);
    return { repCount: rows.length, pending, leaveDays };
  }, [monthSummary]);

  return (
    <div className="admin-page space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <CalendarCheck className="w-6 h-6 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Leave Requests</h1>
            <p className="text-sm text-slate-500">
              Monthly team calendar and per-rep request detail
              {isHierarchyViewer ? ' (your reportees)' : ''}.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-2 text-sm font-semibold text-slate-700"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {isHierarchyViewer && (
        <div className="flex items-center gap-2 text-sm text-blue-800 bg-blue-50 border border-blue-100 rounded-lg px-4 py-2">
          <Users className="w-4 h-4 shrink-0" />
          Showing leave for reps who report to you in your division.
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {VIEW_MODES.map((mode) => {
          const Icon = mode.icon;
          const active = viewMode === mode.id;
          return (
            <button
              key={mode.id}
              type="button"
              onClick={() => setViewMode(mode.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition ${
                active
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
            >
              <Icon className="w-4 h-4" />
              {mode.label}
            </button>
          );
        })}
      </div>

      {viewMode === 'monthly' ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-slate-100 p-4 shadow-sm">
            <p className="text-sm text-slate-500">{isHierarchyViewer ? 'Reportees' : 'Reps'} in view</p>
            <p className="text-2xl font-bold text-slate-900">{monthlyStats.repCount}</p>
          </div>
          <div className="bg-white rounded-lg border border-amber-100 p-4 shadow-sm">
            <p className="text-sm text-amber-700">Pending requests</p>
            <p className="text-2xl font-bold text-amber-800">{monthlyStats.pending}</p>
          </div>
          <div className="bg-white rounded-lg border border-emerald-100 p-4 shadow-sm">
            <p className="text-sm text-emerald-700">Leave days (month)</p>
            <p className="text-2xl font-bold text-emerald-800">{monthlyStats.leaveDays}</p>
          </div>
        </div>
      ) : null}

      <div className="bg-white rounded-lg border border-slate-100 p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">{repFilterLabel}</label>
            <select
              value={selectedRepId}
              onChange={(e) => setSelectedRepId(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            >
              <option value="">
                {isHierarchyViewer ? 'All reportees' : 'All reps'}
              </option>
              {reps.map((rep) => (
                <option key={rep.id} value={rep.id}>
                  {rep.name}
                </option>
              ))}
            </select>
          </div>

          {viewMode === 'monthly' ? (
            <>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Month (IST)</label>
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Status filter</label>
                <div className="flex flex-wrap gap-2">
                  {STATUS_TABS.map((t) => (
                    <button
                      key={t.id || 'all'}
                      type="button"
                      onClick={() => setStatus(t.id)}
                      className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                        status === t.id
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="md:col-span-3">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Status</label>
                <div className="flex flex-wrap gap-2">
                  {STATUS_TABS.map((t) => (
                    <button
                      key={t.id || 'all'}
                      type="button"
                      onClick={() => setStatus(t.id)}
                      className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                        status === t.id
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">From date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">To date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>
            </>
          )}
        </div>

        {viewMode === 'monthly' && (
          <p className="text-xs text-slate-500">
            {formatIstMonthYear(selectedMonth)} — click a day or rep name to open request detail.
          </p>
        )}
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="py-16 text-center text-slate-400">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-3 text-blue-600" />
          Loading…
        </div>
      ) : viewMode === 'monthly' ? (
        <>
          <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600">
            <span className="inline-flex items-center gap-1.5">
              <span className={`w-3 h-3 rounded-sm ${DAY_CELL_STYLES.approved}`} /> Approved
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className={`w-3 h-3 rounded-sm ${DAY_CELL_STYLES.pending}`} /> Pending
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className={`w-3 h-3 rounded-sm ${DAY_CELL_STYLES.rejected}`} /> Rejected
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className={`w-3 h-3 rounded-sm ${DAY_CELL_STYLES.none}`} /> No leave
            </span>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 admin-table-shell">
            <div className="admin-table-scroll">
              <table className="admin-data-table-xl text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase sticky left-0 bg-slate-50 z-10 min-w-[140px]">
                      {isHierarchyViewer ? 'Reportee' : 'Rep'}
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-semibold text-slate-600 uppercase w-14">
                      Req.
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-semibold text-slate-600 uppercase w-12">
                      Days
                    </th>
                    {monthDayNums.map((d) => (
                      <th
                        key={d}
                        className={`px-1 py-2 text-center text-xs font-semibold w-7 ${
                          ymdForDay(selectedMonth, d) === todayYmd
                            ? 'text-blue-700 bg-blue-50'
                            : 'text-slate-500'
                        }`}
                      >
                        {d}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {monthlyRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={monthDayNums.length + 3}
                        className="px-6 py-8 text-center text-slate-600"
                      >
                        No {isHierarchyViewer ? 'reportees' : 'reps'} match your filters.
                      </td>
                    </tr>
                  ) : (
                    monthlyRows.map((row) => (
                      <tr key={row.rep_id} className="hover:bg-slate-50/60">
                        <td className="px-3 py-2 sticky left-0 bg-white z-10 border-r border-slate-100">
                          <button
                            type="button"
                            onClick={() => openRepMonthDetail(row.rep_id)}
                            className="text-left font-semibold text-blue-700 hover:underline"
                          >
                            {row.rep_name || '—'}
                          </button>
                          <div className="text-xs text-slate-400 truncate max-w-[160px]">
                            {row.rep_email}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center font-medium text-slate-700">
                          {row.request_count ?? 0}
                        </td>
                        <td className="px-2 py-2 text-center font-semibold text-slate-800">
                          {row.leave_days ?? 0}
                        </td>
                        {monthDayNums.map((d) => {
                          const dayYmd = ymdForDay(selectedMonth, d);
                          const dayEntry = row.days?.[dayYmd];
                          const cellStatus = dayLeaveCellStatus(dayEntry, dayYmd, todayYmd);
                          return (
                            <td key={d} className="px-0.5 py-1 text-center">
                              <button
                                type="button"
                                title={dayLeaveTooltip(dayEntry)}
                                onClick={() => openRepDayDetail(row.rep_id, dayYmd)}
                                className={`w-5 h-5 rounded-sm mx-auto block ${DAY_CELL_STYLES[cellStatus]} hover:opacity-80`}
                                aria-label={`${row.rep_name} ${dayYmd}`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : filteredItems.length === 0 ? (
        <div className="py-16 text-center text-slate-400">No leave requests found.</div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 admin-table-shell">
          <div className="admin-table-scroll">
            <table className="admin-data-table-wide text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">Rep</th>
                  <th className="px-4 py-3 font-semibold">Type</th>
                  <th className="px-4 py-3 font-semibold">From</th>
                  <th className="px-4 py-3 font-semibold">To</th>
                  <th className="px-4 py-3 font-semibold">Reason</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredItems.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{r.rep_name}</div>
                      <div className="text-xs text-slate-400">{r.rep_email}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{cap(r.leave_type)}</td>
                    <td className="px-4 py-3 text-slate-700">{formatIstDate(r.from_date)}</td>
                    <td className="px-4 py-3 text-slate-700">{formatIstDate(r.to_date)}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-[220px]">
                      <div className="truncate" title={r.reason || ''}>{r.reason || '—'}</div>
                      {r.review_note && (
                        <div className="text-xs text-slate-400 italic mt-0.5">Note: {r.review_note}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold border ${
                          STATUS_STYLES[r.status] || ''
                        }`}
                      >
                        {cap(r.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {r.status === 'pending' && canManageLeave ? (
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          <button
                            type="button"
                            disabled={busyId === r.id}
                            onClick={() => review(r.id, 'approved')}
                            className="px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            <Check className="w-3.5 h-3.5" /> Approve
                          </button>
                          <button
                            type="button"
                            disabled={busyId === r.id}
                            onClick={() => review(r.id, 'rejected')}
                            className="px-2.5 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            <X className="w-3.5 h-3.5" /> Reject
                          </button>
                        </div>
                      ) : r.status === 'pending' ? (
                        <div className="text-right text-xs text-slate-400">View only</div>
                      ) : (
                        <div className="text-right text-xs text-slate-400">—</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveManagement;
